import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Loader2, Swords } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KIND_LABEL, POOL_OPTIONS } from "@/lib/decks";
import { EmptyState, PageHeader } from "@/components/common/PageParts";
import { VisibilityBadge } from "@/components/common/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function DeckCard({ deck, to }) {
  return (
    <Link to={to || `/decks/${deck.deck_id}`} className="panel group block overflow-hidden transition-[border-color,box-shadow] duration-300 hover:border-aether-400/60 hover:shadow-[0_0_20px_rgba(34,211,238,0.15)]" data-testid={`deck-card-${deck.deck_id}`}>
      <div className="relative h-32 overflow-hidden bg-[#0A1222]">
        {deck.cover_image_url && <img src={assetUrl(deck.cover_image_url)} alt="" className="h-full w-full object-cover object-top opacity-70 transition-opacity group-hover:opacity-90" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#101828] to-transparent" />
        <div className="absolute left-3 top-3 flex gap-1.5">
          {!deck.published && <span className="rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] uppercase text-copper-300">Draft</span>}
          {deck.visibility !== "public" && <VisibilityBadge visibility={deck.visibility} />}
        </div>
      </div>
      <div className="p-4">
        <h3 className="line-clamp-1 font-heading text-base text-amber-100 group-hover:text-aether-200">{deck.name}</h3>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-slate-500">
          {deck.format_label} · {deck.card_count} cards · {KIND_LABEL[deck.kind]}{deck.owner ? ` · @${deck.owner.username}` : ""}
        </p>
      </div>
    </Link>
  );
}

function CreateDeckDialog() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", format_id: "fmt_commander", card_pool: "any", visibility: "public", description: "" });
  const { data: formats = [] } = useQuery({ queryKey: ["formats"], queryFn: () => api.get("/formats").then((r) => r.data.items) });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, format: "commander" };
      const { data } = await api.post("/decks", payload);
      navigate(`/decks/${data.deck_id}/build`);
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };
  const sel = (key, options, label) => (
    <div>
      <label className="field-label" htmlFor={`deck-${key}`}>{label}</label>
      <select id={`deck-${key}`} className="field capitalize" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`create-deck-${key}-select`}>
        {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="btn-gold" data-testid="create-deck-button"><Plus className="h-4 w-4" />New deck</button></DialogTrigger>
      <DialogContent className="border-[#2A3447] bg-[#101828] text-slate-200" data-testid="create-deck-dialog">
        <DialogHeader><DialogTitle className="font-heading text-amber-100">Start a new deck</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="field-label" htmlFor="deck-name">Deck name</label>
            <input id="deck-name" required maxLength={100} className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-deck-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">{sel("format_id", formats.map((f) => ({ value: f.format_id, label: f.name })), "Format")}{sel("card_pool", POOL_OPTIONS, "Card pool")}</div>
          <Link to="/formats" className="text-xs text-aether-300 hover:underline" data-testid="create-deck-formats-link">Need different rules? Build a custom format</Link>
          {sel("visibility", ["public", "unlisted", "private"], "Visibility")}
          <button type="submit" className="btn-gold w-full" disabled={busy} data-testid="create-deck-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Create & open builder</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeckGrid({ queryKey, url, empty, build }) {
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => api.get(url).then((r) => r.data.items) });
  if (isLoading) return null;
  if (!data.length) return <EmptyState icon={Swords} title={empty} testid={`${queryKey[1]}-decks-empty`} />;
  return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{data.map((d) => <DeckCard key={d.deck_id} deck={d} to={build ? `/decks/${d.deck_id}/build` : undefined} />)}</div>;
}

export default function Decks() {
  const { user } = useAuth();
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Deck builder" title="Decks" testid="decks-header"
        description="Build Commander, 60-card constructed or custom-format decks from custom cards, official cards, or both."
        actions={<>
          <Link to="/formats" className="btn-outline" data-testid="decks-formats-link">Formats</Link>
          {user ? <CreateDeckDialog /> : <Link to="/login" className="btn-outline" data-testid="decks-login-link">Sign in to build decks</Link>}
        </>} />
      <Tabs key={user ? "member" : "guest"} defaultValue={user ? "mine" : "community"}>
        <TabsList className="mb-8 bg-[#101828]">
          {user && <TabsTrigger value="mine" data-testid="decks-tab-mine">My decks</TabsTrigger>}
          <TabsTrigger value="community" data-testid="decks-tab-community">Published decks</TabsTrigger>
        </TabsList>
        {user && <TabsContent value="mine"><DeckGrid queryKey={["decks", "mine"]} url="/me/decks" empty="No decks yet — start one above" build /></TabsContent>}
        <TabsContent value="community"><DeckGrid queryKey={["decks", "community"]} url="/decks" empty="No published decks yet" /></TabsContent>
      </Tabs>
    </div>
  );
}
